import { Injectable } from '@angular/core';
import { HttpBackend, HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, throwError } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { CommonResponse, OneTimeToken, User } from 'app/server/models';
import { ActivatedRoute, Router } from '@angular/router';
import { CoreMenu } from '@core/types';
import { environment } from '../../../environments/environment';
import { ChangePassword } from '../models/change-password/change-password';
import { ProfileUser } from '../models/profile/profile';
import { CurrentUniversityService } from './current-university.service';
import { CurrentSchoolUniversityService } from './current-school-university.service';
import { ProfileType } from '../enums/profile-type';
import { RoleType } from '../enums/role-type';
import { CurrentCareerService } from './current-career.service';
import { TranslateService } from '@ngx-translate/core';
import Swal from 'sweetalert2';

@Injectable({ providedIn: 'root' })
export class AuthenticationService {
	// public
	public currentUser: Observable<User | null>;
	public refreshingToken: boolean = false;
	_http: HttpClient;
	frontendMenu: Array<CoreMenu> = [];
	public Loginloading: boolean = false;

	// private
	private currentUserSubject: BehaviorSubject<string | null>;
	public currentUserObj: BehaviorSubject<User | null>;
	public currentActiveProfile: BehaviorSubject<ProfileUser | null>;
	activeProfileId: BehaviorSubject<string | null>;
	public userLoaded: BehaviorSubject<boolean>;

	constructor(
		handler: HttpBackend,
		private _router: Router,
		private _route: ActivatedRoute,
		private _currentSchoolUniversityService: CurrentSchoolUniversityService,
		private _currentUniversityService: CurrentUniversityService,
		private _currentCareer: CurrentCareerService,
		private _translateService: TranslateService
	) {
		this._http = new HttpClient(handler);
		this.currentUserSubject = new BehaviorSubject<string | null>(JSON.parse(sessionStorage.getItem('currentUser') || 'null'));
		this.currentUserObj = new BehaviorSubject<User | null>(null);
		this.currentActiveProfile = new BehaviorSubject<ProfileUser | null>(null);
		this.activeProfileId = new BehaviorSubject<string | null>(null);
		this.userLoaded = new BehaviorSubject<boolean>(false);

		this.currentUser = this.currentUserObj.asObservable();
		if (this.currentUserValue) {
			if (this.getActiveProfile()) {
				this.activeProfileId.next(String(this.getActiveProfile()));
			}

			this.getCurrentUser();
		} else {
			this.userLoaded.next(true);
		}
	}

	// getter: currentUserValue
	public get currentUserValue(): string | null {
		return this.currentUserSubject.value;
	}

	/**
	 * Resolves when the user data from /auth/me has finished loading (userLoaded = true).
	 * Guards must await this before reading currentActiveProfile: after a page refresh
	 * the profile is repopulated asynchronously and reading it too early causes
	 * false "not authorized" redirects.
	 */
	waitForUserLoaded(): Promise<void> {
		if (this.userLoaded.value) {
			return Promise.resolve();
		}

		return new Promise<void>(resolve => {
			const sub = this.userLoaded.subscribe(loaded => {
				if (loaded) {
					sub.unsubscribe();
					resolve();
				}
			});
		});
	}

	/**
	 * User login
	 *
	 * @param request
	 * @returns user
	 */
	login(request: { email: string; password: string; university_id: string }): Observable<{ passwordExpiredToken: string } | void> {
		return this._http.post<any>(`${environment.apiUrl}/auth/login`, request).pipe(
			map((data: any): { passwordExpiredToken: string } | void => {
				const accessToken = data?.data?.access_token ?? data?.access_token ?? data?.token;
				const tokenType = data?.data?.token_type ?? data?.token_type;

				// Expired password: the backend returns a one-time reset token (not a bearer token).
				// It is valid only for POST /auth/password/reset, so it must never become a session.
				if (accessToken && tokenType === 'ott') {
					this.deleteSession();
					return { passwordExpiredToken: accessToken };
				}

				if (accessToken) {
					sessionStorage.setItem('currentUser', JSON.stringify(accessToken));
					sessionStorage.setItem('isSSO', 'false');

					// notify
					this.currentUserSubject.next(accessToken);
					this.getCurrentUser();
				} else {
					this.deleteSession();
					throw new Error('Risposta login non valida: token mancante');
				}

				return;
			})
		);
	}

	goToSSO(tenant_uuid: string) {
		const returnTo = encodeURIComponent(`${window.location.origin}/authentication/login`);
		window.location.replace(`${environment.baseUrl}/saml2/${tenant_uuid}/login?returnTo=${returnTo}`);
	}

	SSOExist() {
		return this._route.snapshot.queryParams['one_time_token'];
	}

	loginSaml2(oneTimeToken: string): Observable<void> {
		return this._http.get<CommonResponse<OneTimeToken>>(`${environment.apiUrl}/auth/retrieve/ott/${oneTimeToken}`).pipe(
			map((data: CommonResponse<OneTimeToken>) => {
				if (!data.data.access_token) {
					throw new Error('sso_failed');
				}
				sessionStorage.setItem('currentUser', JSON.stringify(data.data.access_token));
				sessionStorage.setItem('isSSO', 'true');
				this.currentUserSubject.next(data.data.access_token);
				this.getCurrentUser();
				return;
			})
		);
	}

	passwordChangeRequest(request: { email: string; university_id: string }): Observable<any> {
		return this._http.post<CommonResponse<ChangePassword>>(`${environment.apiUrl}/auth/password/change_request`, request);
	}

	setNewPassword(request: { one_time_token: string; password: string }) {
		return this._http.post<CommonResponse<any>>(`${environment.apiUrl}/auth/password/reset`, request);
	}

	getCareersFilteredByAuthUser(profileId?: string, schoolUniversityId?: string, universityId?: string): Observable<CommonResponse<any>> {
		if (profileId && schoolUniversityId) {
			const currentUser = (sessionStorage.getItem('currentUser') || '').replace(/^['\"](.*)['\"]$/, '$1');
			return this._http.get<CommonResponse<any>>(`${environment.baseUrl}/api${environment.apiSchoolUrl}/careers/filtered_by/auth_user`, {
				headers: {
					Authorization: `Bearer ${currentUser}`,
					'X-Profile': profileId,
					'X-University': universityId ?? this._currentUniversityService.currentUniversity?.id ?? '',
					'X-SchoolUniversity': schoolUniversityId,
				},
			});
		} else {
			return this._http.get<CommonResponse<any>>(`${environment.apiSchoolUrl}/careers/filtered_by/auth_user`);
		}
	}

	deleteSession() {
		sessionStorage.removeItem('currentUser');
		sessionStorage.removeItem('isSSO');
		sessionStorage.removeItem('activeProfile');
		this.activeProfileId.next(null);
		sessionStorage.removeItem('university');
		sessionStorage.removeItem('school');
		sessionStorage.removeItem('career');
		this._currentCareer.currentCareerId.next(null as any);
		this._currentCareer.currentCareer.next(null as any);
		this.currentUserSubject.next(null);
		this.currentUserObj.next(null);
		this.currentActiveProfile.next(null);
		this.userLoaded.next(true);
	}

	/**
	 * User logout
	 *
	 */
	logout() {
		const isSSO = sessionStorage.getItem('isSSO') === 'true';
		const university = this._currentUniversityService.currentUniversity;
		const tenantUuid = university?.saml2_tenant?.uuid;

		if (isSSO && tenantUuid) {
			Swal.fire({
				title: this._translateService.instant('common_swal_logout_title'),
				text: this._translateService.instant('common_swal_how_to_logout_text'),
				icon: 'question',
				showCancelButton: true,
				showDenyButton: true,
				confirmButtonColor: '#7367F0',
				denyButtonColor: '#4B5563',
				cancelButtonColor: '#E42728',
				confirmButtonText: this._translateService.instant('common_swal_logout_university_only_btn'),
				denyButtonText: this._translateService.instant('common_swal_logout_entire_federation_btn'),
				cancelButtonText: this._translateService.instant('common_btn_cancel'),
				customClass: {
					confirmButton: 'btn btn-primary me-1',
					denyButton: 'btn btn-secondary me-1',
					cancelButton: 'btn btn-danger',
				},
				buttonsStyling: false,
			}).then(result => {
				if (result.isConfirmed) {
					this.deleteSession();
					window.location.href = 'authentication/login';
				} else if (result.isDenied) {
					this.deleteSession();
					window.location.replace(`${environment.baseUrl}/saml2/${tenantUuid}/logout`);
				}
			});
		} else {
			this.deleteSession();
			window.location.href = 'authentication/login';
		}
	}

	menuFilter(menu: CoreMenu[], role_id: string) {
		if (this.currentUserObj.value?.fiscal_code) {
			const payroll = menu.find(res => res.id === 'bustepaga_cud');
			if (payroll?.children?.[0]) {
				payroll.children[0].url = `/request/payroll/${this.currentUserValue || ''}`;
			}
		} else {
			menu = menu.filter(res => res.id !== 'bustepaga_cud');
		}

		return menu.filter(item => this.itemMenuFilter(item, role_id));
	}

	getLoggedUser(): Observable<User> {
		return this._http
			.get<CommonResponse<User> | User>(`${environment.apiUrl}/auth/me`, {
				headers: {
					Authorization: `Bearer ${this.currentUserValue}`,
				},
			})
			.pipe(
				map((res: CommonResponse<User> | User) => {
					const maybeResponse = res as CommonResponse<User>;
					return maybeResponse?.data ?? (res as User);
				})
			);
	}

	private getCurrentUser(): void {
		this.userLoaded.next(false);
		this.refreshCurrentUser();
	}

	/**
	 * Refresh the current user data from the backend (permissions, roles, etc.)
	 * and update the in-memory BehaviorSubject.
	 */
	refreshCurrentUser(): void {
		this.getLoggedUser().subscribe({
			next: res => {
				const user = res;
				
				if (user) {
					// Check if we are currently accessing via a specific university subdomain
					const hasActiveUniversity = !!this._currentUniversityService.currentUniversity;
					
					// Filter user profiles based on the current navigation context (Global vs University-specific)
					user.profile_users = (user.profile_users || []).filter(pu => {
						const isSuperAdmin = pu.profile_id === RoleType.SuperAdmin;
						
						if (hasActiveUniversity) {
							// In a university subdomain (e.g. unisr.didact.it):
							// - Keep the global SuperAdmin profile.
							// - Keep university-specific profiles only if they belong to this university.
							//   (The backend's withProfileUserUniversityAuth scope filters 'profile_user_university',
							//   leaving it null for profiles associated with other universities).
							return isSuperAdmin || !!pu.profile_user_university;
						}
						
						// On the global domain (e.g. ssm.didact.it):
						// - Keep only the global SuperAdmin profile.
						return isSuperAdmin;
					});
				}

				this.currentUserObj.next(user);
				
				const profileUsers = user?.profile_users || [];
				if (profileUsers.length === 0) {
					return;
				}

				// Automatic profile selection optimization:
				// If the user has exactly 1 valid profile left after filtering, and that profile
				// is either global (no university/schools) or has at most one school associated,
				// we can select it automatically without asking the user.
				const hasSingleProfile = profileUsers.length === 1;
				const schoolCount = profileUsers[0]?.profile_user_university?.profile_user_university_school_universities?.length ?? 0;

				if (hasSingleProfile && schoolCount <= 1) {
					this.chooseProfile(0);
				} else if (this.activeProfileId.value) {
					// Otherwise, if the user had a previously active profile, try to restore it
					const index = profileUsers.findIndex(i => i.profile_id === this.activeProfileId.value);
					if (index >= 0) {
						this.chooseProfile(index);
					}
				}
				this.userLoaded.next(true);
			},
			error: error => {
				console.error('Error fetching current user:', error);
				this.userLoaded.next(true);
				if (error.status === 401 || error.status === 403) {
					this.logout();
				}
			},
		});
	}

	chooseProfile(profileUserIndex: number, schoolUniversityId: string | null = null, careerId?: string) {
		const profileUsers = this.currentUserObj.value?.profile_users || [];
		const selectedProfile = profileUsers[profileUserIndex];
		if (!selectedProfile?.profile) {
			return;
		}

		const profileTypeId = selectedProfile.profile.profile_type_id;
		if (profileTypeId === ProfileType.School) {
			if (selectedProfile?.profile_user_university?.university) {
				this._currentUniversityService.changeUniversity(selectedProfile.profile_user_university.university, false);
			}

			if (schoolUniversityId) {
				const schoolUniversity = selectedProfile?.profile_user_university?.profile_user_university_school_universities?.find(
					i => i.school_university_id === schoolUniversityId
				);
				if (schoolUniversity?.school_university) {
					this._currentSchoolUniversityService.changeSchool(schoolUniversity.school_university, false);
				}
			} else if (this._currentSchoolUniversityService.currentSchoolId.value) {
				const schoolUniversity = selectedProfile?.profile_user_university?.profile_user_university_school_universities?.find(
					i => i.school_university_id === this._currentSchoolUniversityService.currentSchoolId.value
				);
				if (schoolUniversity?.school_university) {
					this._currentSchoolUniversityService.changeSchool(schoolUniversity.school_university, false);
				}
			} else {
				if (selectedProfile?.profile_user_university?.profile_user_university_school_universities?.[0]?.school_university) {
					this._currentSchoolUniversityService.changeSchool(
						selectedProfile.profile_user_university.profile_user_university_school_universities[0].school_university,
						false
					);
				}
			}
		} else if (profileTypeId === ProfileType.University) {
			if (selectedProfile?.profile_user_university?.university) {
				this._currentUniversityService.changeUniversity(selectedProfile.profile_user_university.university, false);
			}
		} else if (profileTypeId === ProfileType.OperationalUnit) {
			if (selectedProfile?.profile_user_university?.university) {
				this._currentUniversityService.changeUniversity(selectedProfile.profile_user_university.university, false);
			}
		} else if (profileTypeId === ProfileType.Multitenant) {
		}

		if (selectedProfile?.profile_id === RoleType.Trainee && careerId) {
			this.changeActiveUser(selectedProfile, careerId);
		} else {
			this.changeActiveUser(selectedProfile);
		}
	}

	private changeActiveUser(user?: ProfileUser, careerId?: string) {
		if (!user) {
			return;
		}

		sessionStorage.setItem('activeProfile', String(user.profile_id));

		this.activeProfileId.next(user.profile_id || null);

		this.currentActiveProfile.next(user);

		if (user.profile_id === RoleType.Trainee && careerId) {
			this.getCareersFilteredByAuthUser(
				this.activeProfileId.value || undefined,
				sessionStorage.getItem('school') || undefined,
				user.profile_user_university?.university_id
			).subscribe(res => {
				const activeCareer = res.data.find((i: any) => i.id === careerId);
				this._currentCareer.changeActiveCareer(activeCareer);
				this.currentActiveProfile.next(user);
			});
		}
	}

	exitActiveUser() {
		sessionStorage.removeItem('activeProfile');
		this.activeProfileId.next(null);
		this.currentActiveProfile.next(null);
	}

	private getActiveProfile(): string | undefined {
		if (sessionStorage.getItem('activeProfile') !== null) {
			return sessionStorage.getItem('activeProfile') || undefined;
		} else {
			return undefined;
		}
	}

	private itemMenuFilter(menuItem: CoreMenu, role_id: string) {
		if (menuItem.children) {
			menuItem.children = menuItem.children.filter(item => this.itemMenuFilter(item, role_id));
		}
		if (menuItem.roles) {
			return menuItem.roles.includes(role_id);
		} else {
			return true;
		}
	}

	filterItems(partialMenu: Array<CoreMenu>, activeProfileId: string): Array<CoreMenu> {
		return partialMenu.filter(item => {
			if (Array.isArray(item.children) && item.children.length > 0 && !item.roles) {
				const children = (item.children || []) as Array<CoreMenu>;
				item.children = this.filterItems(children, activeProfileId);
				return item.children?.length > 0;
			} else {
				if (!item?.roles) {
					return true;
				} else {
					if (item.roles.includes(activeProfileId)) {
						return true;
					} else {
						return false;
					}
				}
				return false;
			}
		});
	}

	filterItemsSection(partialMenu: Array<CoreMenu>, activeProfile: ProfileUser): Array<CoreMenu> {
		return partialMenu.filter(item => {
			if (Array.isArray(item.children) && item.children.length > 0) {
				const children = (item.children || []) as Array<CoreMenu>;
				item.children = this.filterItemsSection(children, activeProfile);
				return item.children?.length > 0;
			} else {
				return true;
			}
		});
	}

	filterCombinedItems(partialMenu: Array<CoreMenu>, activeProfile: ProfileUser): Array<CoreMenu> {
		return partialMenu.filter(item => {
			// Check Spatie permissions first
			if (item.permissions && item.permissions.length > 0) {
				const user = this.currentUserObj?.value;
				if (user) {
					const userPermissions = user.permissions || [];
					const hasPermission = item.permissions.some((p: string) =>
						userPermissions.some((up: any) => (typeof up === 'string' ? up : up.name) === p)
					);
					if (!hasPermission) return false;
				} else {
					return false;
				}
			}

			if (Array.isArray(item.children) && item.children.length > 0 && !item.roles) {
				const children = (item.children || []) as Array<CoreMenu>;
				item.children = this.filterCombinedItems(children, activeProfile);
				return item.children?.length > 0;
			} else {
				if (item.roles) {
					return activeProfile?.profile_id ? item.roles.includes(activeProfile.profile_id) : false;
				} else {
					return true;
				}
			}
		});
	}
}
